/**
 * @fileoverview Data Collection frontend service
 *
 * API client for collection templates, records, photos, stats, and promotions.
 *
 * @author FacilityAI
 * @version 1.0.0
 */

import { CORE_URL } from "@/constants/api";
import { VITE_CORE_API_URL } from "@/constants/env";
import { auth } from "@/services/auth-service";

const BASE = `${VITE_CORE_API_URL}${CORE_URL}/organization/data-collection`;

// ─── helpers ────────────────────────────────────────────────────────────────

async function getToken(): Promise<string> {
	const token = await auth?.currentUser?.getIdToken();
	if (!token) throw new Error("Authentication required");
	return token;
}

async function apiGet<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPost<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "POST",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiPut<T>(path: string, body: unknown): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "PUT",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
		body: JSON.stringify(body),
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

async function apiDelete<T>(path: string): Promise<T> {
	const token = await getToken();
	const res = await fetch(`${BASE}${path}`, {
		method: "DELETE",
		headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
	});
	const json = await res.json();
	if (!json.success) throw new Error(json.message ?? "Request failed");
	return json.data;
}

// ═══════════════════════════════════════════════════════════════════════════
// TYPES (mirror backend models)
// ═══════════════════════════════════════════════════════════════════════════

export interface CollectionTemplate {
	id: number;
	organizationId: number;
	name: string;
	description: string | null;
	category: string | null;
	icon: string | null;
	requiresPhoto: boolean;
	healthWeight: number;
	isActive: boolean;
	createdAt: string;
	updatedAt: string;
}

export interface CollectionTemplateField {
	id: number;
	templateId: number;
	fieldKey: string;
	fieldLabel: string;
	fieldType: string;
	isRequired: boolean;
	aiExtractable: boolean;
	options: string[] | null;
	config: Record<string, unknown> | null;
	displayOrder: number;
	isHealthField: boolean;
	healthMapping: Record<string, number> | null;
}

export interface CollectionTemplateWithFields extends CollectionTemplate {
	fields: CollectionTemplateField[];
}

export interface CollectionRecord {
	id: number;
	organizationId: number;
	templateId: number;
	locationId: number;
	collectedByUserId: number | null;
	reviewedByUserId: number | null;
	status: string;
	primaryPhotoUrl: string | null;
	aiExtractedData: Record<string, { value: unknown; confidence: number }> | null;
	finalData: Record<string, unknown> | null;
	healthScoreContribution: number | null;
	areaName: string | null;
	notes: string | null;
	assetPromotionStatus: string | null;
	promotedAssetId: number | null;
	createdAt: string;
	updatedAt: string;
	// Joins
	templateName?: string;
	templateCategory?: string;
	locationName?: string;
	collectedByName?: string;
	reviewedByName?: string;
	photoCount?: number;
	photos?: CollectionRecordPhoto[];
}

export interface CollectionRecordPhoto {
	id: number;
	recordId: number;
	photoUrl: string;
	photoType: string;
	caption: string | null;
	aiAnalysis: Record<string, unknown> | null;
	createdAt: string;
}

export interface CollectionStats {
	total: number;
	pending: number;
	processing: number;
	completed: number;
	reviewed: number;
	flagged: number;
	byCategory: Record<string, number>;
	promotionSuggestions: number;
}

// ═══════════════════════════════════════════════════════════════════════════
// TEMPLATE API
// ═══════════════════════════════════════════════════════════════════════════

export async function getTemplates(includeInactive = false): Promise<CollectionTemplate[]> {
	return apiGet<CollectionTemplate[]>(`/templates${includeInactive ? "?includeInactive=true" : ""}`);
}

export async function getTemplateById(id: number): Promise<CollectionTemplateWithFields> {
	return apiGet<CollectionTemplateWithFields>(`/templates/${id}`);
}

export async function createTemplate(data: {
	name: string;
	description?: string;
	category?: string;
	icon?: string;
	requiresPhoto?: boolean;
	healthWeight?: number;
}): Promise<CollectionTemplate> {
	return apiPost<CollectionTemplate>("/templates", data);
}

export async function updateTemplate(
	id: number,
	data: Partial<{
		name: string;
		description: string;
		category: string;
		icon: string;
		requiresPhoto: boolean;
		healthWeight: number;
		isActive: boolean;
	}>,
): Promise<CollectionTemplate> {
	return apiPut<CollectionTemplate>(`/templates/${id}`, data);
}

export async function deleteTemplate(id: number): Promise<void> {
	await apiDelete(`/templates/${id}`);
}

// ─── Field API ───────────────────────────────────────────────────────────

export async function addField(
	templateId: number,
	data: {
		fieldKey: string;
		fieldLabel: string;
		fieldType: string;
		isRequired?: boolean;
		aiExtractable?: boolean;
		options?: string[];
		config?: Record<string, unknown>;
		displayOrder?: number;
		isHealthField?: boolean;
		healthMapping?: Record<string, number>;
	},
): Promise<CollectionTemplateField> {
	return apiPost<CollectionTemplateField>(`/templates/${templateId}/fields`, data);
}

export async function updateField(
	templateId: number,
	fieldId: number,
	data: Partial<{
		fieldLabel: string;
		fieldType: string;
		isRequired: boolean;
		aiExtractable: boolean;
		options: string[];
		config: Record<string, unknown>;
		displayOrder: number;
		isHealthField: boolean;
		healthMapping: Record<string, number>;
	}>,
): Promise<CollectionTemplateField> {
	return apiPut<CollectionTemplateField>(`/templates/${templateId}/fields/${fieldId}`, data);
}

export async function deleteField(templateId: number, fieldId: number): Promise<void> {
	await apiDelete(`/templates/${templateId}/fields/${fieldId}`);
}

export async function reorderFields(
	templateId: number,
	orders: Array<{ fieldId: number; displayOrder: number }>,
): Promise<void> {
	await apiPut(`/templates/${templateId}/fields/reorder`, { orders });
}

// ═══════════════════════════════════════════════════════════════════════════
// RECORD API
// ═══════════════════════════════════════════════════════════════════════════

export async function getRecords(filters?: {
	status?: string;
	templateId?: number;
	locationId?: number;
	limit?: number;
	offset?: number;
}): Promise<{ records: CollectionRecord[]; total: number }> {
	const params = new URLSearchParams();
	if (filters?.status) params.set("status", filters.status);
	if (filters?.templateId) params.set("templateId", String(filters.templateId));
	if (filters?.locationId) params.set("locationId", String(filters.locationId));
	if (filters?.limit) params.set("limit", String(filters.limit));
	if (filters?.offset) params.set("offset", String(filters.offset));
	const qs = params.toString();
	return apiGet<{ records: CollectionRecord[]; total: number }>(`/records${qs ? `?${qs}` : ""}`);
}

export async function getRecordById(id: number): Promise<CollectionRecord> {
	return apiGet<CollectionRecord>(`/records/${id}`);
}

export async function getRecordsByLocation(locationId: number, limit = 10): Promise<CollectionRecord[]> {
	return apiGet<CollectionRecord[]>(`/records/location/${locationId}?limit=${limit}`);
}

export async function getRecordsByTemplate(templateId: number): Promise<CollectionRecord[]> {
	return apiGet<CollectionRecord[]>(`/records/template/${templateId}`);
}

export async function createRecord(data: {
	templateId: number;
	locationId: number;
	primaryPhotoUrl?: string;
	areaName?: string;
	notes?: string;
	fieldValues?: Record<string, unknown>;
}): Promise<CollectionRecord> {
	return apiPost<CollectionRecord>("/records", data);
}

export async function updateRecordData(
	id: number,
	data: { finalData: Record<string, unknown>; notes?: string; areaName?: string },
): Promise<CollectionRecord> {
	return apiPut<CollectionRecord>(`/records/${id}`, data);
}

export async function reviewRecord(id: number): Promise<CollectionRecord> {
	return apiPut<CollectionRecord>(`/records/${id}/review`, {});
}

export async function deleteRecord(id: number): Promise<void> {
	await apiDelete(`/records/${id}`);
}

export async function addRecordPhoto(
	id: number,
	data: { photoUrl: string; photoType?: string; caption?: string },
): Promise<CollectionRecordPhoto> {
	return apiPost<CollectionRecordPhoto>(`/records/${id}/photos`, data);
}

export async function getRecordPhotos(id: number): Promise<CollectionRecordPhoto[]> {
	return apiGet<CollectionRecordPhoto[]>(`/records/${id}/photos`);
}

// ═══════════════════════════════════════════════════════════════════════════
// STATS API
// ═══════════════════════════════════════════════════════════════════════════

export async function getCollectionStats(locationId?: number): Promise<CollectionStats> {
	const qs = locationId ? `?locationId=${locationId}` : "";
	return apiGet<CollectionStats>(`/records/stats${qs}`);
}

// ═══════════════════════════════════════════════════════════════════════════
// PROMOTION API
// ═══════════════════════════════════════════════════════════════════════════

export async function getPromotionSuggestions(limit = 50, offset = 0): Promise<CollectionRecord[]> {
	return apiGet<CollectionRecord[]>(`/promotions/suggestions?limit=${limit}&offset=${offset}`);
}

export async function promoteToAsset(
	recordId: number,
	data: {
		assetTag?: string;
		organizationAssetTypeId?: number;
		organizationEquipmentAreaId?: number;
		notes?: string;
	},
): Promise<{ assetId: number; recordId: number }> {
	return apiPost<{ assetId: number; recordId: number }>(`/promotions/${recordId}/promote`, data);
}

export async function dismissPromotion(recordId: number): Promise<void> {
	await apiPost(`/promotions/${recordId}/dismiss`, {});
}

// ═══════════════════════════════════════════════════════════════════════════
// HEALTH API
// ═══════════════════════════════════════════════════════════════════════════

export interface CollectionHealthSummary {
	locationId: number;
	collectionHealthScore: number;
	recordCount: number;
	categoryBreakdown: Array<{
		category: string;
		avgScore: number;
		recordCount: number;
	}>;
}

export async function getCollectionHealthByLocation(locationId: number): Promise<CollectionHealthSummary> {
	return apiGet<CollectionHealthSummary>(`/health/location/${locationId}`);
}

export async function getCollectionHealthScore(locationId: number): Promise<{ locationId: number; collectionHealthScore: number | null }> {
	return apiGet<{ locationId: number; collectionHealthScore: number | null }>(`/health/location/${locationId}/score`);
}
